import { useMemo, useState } from "react"

import { cloneDeep } from "lodash"
import { PlusIcon, Trash, XIcon } from "lucide-react"
import { Button, DefaultPopover, Separator, Input, Select, Label } from "twyne-new.ui"

import { useCraftProp } from "../utils"

export default function AttributesProperty({ hiddenAttributes: hiddenAttributesProp = [] }) {
  const { value = {}, setValue } = useCraftProp('attributes')
  const [preview, setPreview] = useState(false)
  const [open, setOpen] = useState(false)

  const handleDelete = key => {
    if (!key) {
      return handleClosePreview()
    }

    const copyAttributes = cloneDeep(value)
    delete copyAttributes[key]
    setValue(copyAttributes)
  }

  const handleAdd = () => {
    setPreview(true)
  }

  const handleClosePreview = () => {
    setPreview(false)
  }

  const hiddenAttributes = useMemo(() => [...hiddenAttributesProp, ...HIDDEN_ATTRIBUTES_DEFAULT], [hiddenAttributesProp])
  const attributes = useMemo(() => Object.fromEntries(Object.entries(value).filter(([key]) => !HIDDEN_ATTRIBUTES_DEFAULT.includes(key))), [value])
  const hasAttributes = useMemo(() => Object.keys(attributes).length > 0, [attributes])

  const items = useMemo(() => {
    const items = []
    Object.entries(attributes).filter(([_, value]) => !!value || value === undefined).forEach(([key, value]) => {
      items.push({ values: { key, value, isDefault: hiddenAttributesProp.includes(key) } })
    })

    if (!hasAttributes || preview) {
      items.push({ values: {} })
    }

    return items
  }, [attributes, hasAttributes, preview, hiddenAttributesProp])

  const handleClose = () => {
    setOpen(false)
    const filteredAttributes = Object.fromEntries(Object.entries(attributes).filter(([_, value]) => value !== undefined))
    setValue(filteredAttributes)
  }

  return <>
    <InputTrigger onClick={() => setOpen(true)} attributes={attributes} />
    <DefaultPopover
      trigger={<div></div>}
      triggerProps={{ asChild: true }}
      contentProps={{ className: 'w-96', side: 'left', align: 'center', onInteractOutside: handleClose }}
      wrapperProps={{ open: open, onOpenChange: open => open ? setOpen(open) : handleClose() }}
    >
      <div className="w-full">
        <div className='flex justify-between items-center'>
          <div className='flex gap-2 items-center '>
            Attributes
          </div>
          <XIcon size='16' className='cursor-pointer' onClick={() => handleClose()} />
        </div>
        <Separator className='m-0 my-2' />

        <div className='flex flex-col gap-2 mb-2'>
          {items.map(({ values, index }) => <AttributeItem
            key={`key-${index}`}
            values={values}
            onDelete={handleDelete}
            hiddenAttributes={hiddenAttributes}
            onClosePreview={handleClosePreview}
          />)}
        </div>
        <div className='flex justify-end'>
          <Button variant='light' size='sm' onClick={handleAdd}> <PlusIcon size='14' /> </Button>
        </div>
      </div>
    </DefaultPopover>
  </>
}

const InputTrigger = ({ onClick, attributes }) => {
  const inputValue = useMemo(() => Object.entries(attributes).map(([key, value]) => key && value ? `${key}: ${value}` : '').filter(Boolean).join(', '), [attributes])
  return <Input value={inputValue || ''} readOnly variant='accent' onClick={onClick} />
}

const AttributeItem = ({ values: { key, value, isDefault }, onDelete, onClosePreview, hiddenAttributes = [] }) => {
  const { value: attributes = {}, setValue: setAttributes } = useCraftProp('attributes')

  const handleChangeKey = newKey => {
    if (!key) {
      onClosePreview()
    }

    const copyAttributes = cloneDeep(attributes || {})
    delete copyAttributes[key]
    copyAttributes[newKey] = value
    setAttributes(copyAttributes)
  }

  const handleChangeValue = value => {
    const copyAttributes = cloneDeep(attributes)
    copyAttributes[key] = value === '' ? undefined : value
    setAttributes(copyAttributes)
  }

  const hiddenOptions = useMemo(() => [...hiddenAttributes, ...Object.keys(attributes)], [attributes, hiddenAttributes])
  const selectOptions = useMemo(() => ATTRIBUTES_OPTIONS.filter(option => !hiddenOptions.includes(option.value)), [hiddenOptions])

  return <>
    <div className="flex gap-1 items-end">
      <div className='w-1/2'>
        <Label>Key</Label>
        <Select
          options={selectOptions}
          value={key}
          onChange={option => handleChangeKey(option?.value)}
          isValidNewOption={value => !hiddenOptions.includes(value)}
          creatable
          disabled={isDefault}
        />
      </div>
      <div className='w-1/2'>
        <Label>Value</Label>
        <Input
          value={value || ''}
          onChange={e => handleChangeValue(e.target.value)}
          disabled={!key || isDefault}
          type='text'
        />
      </div>

      <Button className='h-9' variant='light' size='sm' onClick={() => onDelete(key)} disabled={isDefault}> <Trash size='14' /> </Button>
    </div>
  </>
}

const HIDDEN_ATTRIBUTES_DEFAULT = ['className', 'class']

const ATTRIBUTES_OPTIONS = [
  { label: "ID", value: "id" },
  { label: "Name", value: "name" },
  { label: "Type", value: "type" },
  { label: "Value", value: "value" },
  { label: "Placeholder", value: "placeholder" },
  { label: "Src", value: "src" },
  { label: "Alt", value: "alt" },
  { label: "Href", value: "href" },
  { label: "Checked", value: "checked" },
  { label: "Disabled", value: "disabled" },
  { label: "Required", value: "required" },
  { label: "Readonly", value: "readonly" },
  { label: "Title", value: "title" },
  { label: "Target", value: "target" },
  { label: "Rel", value: "rel" },
  { label: "Hidden", value: "hidden" },
  { label: "For", value: "for" },
  { label: "Multiple", value: "multiple" },
  { label: "Max Length", value: "maxlength" },
  { label: "Min Length", value: "minlength" },
  { label: "Min", value: "min" },
  { label: "Max", value: "max" },
  { label: "Step", value: "step" },
  { label: "Pattern", value: "pattern" },
  { label: "Autocomplete", value: "autocomplete" },
  { label: "Action", value: "action" },
  { label: "Method", value: "method" },
  { label: "Enctype", value: "enctype" },
  { label: "Tabindex", value: "tabindex" },
  { label: "Draggable", value: "draggable" },
  { label: "Content Editable", value: "contenteditable" },
  { label: "Access Key", value: "accesskey" },
  { label: "Role", value: "role" },
  { label: "Aria", value: "aria-*" },
  { label: "Direction", value: "dir" },
  { label: "Language", value: "lang" },
  { label: "Download", value: "download" },
  { label: "Controls", value: "controls" },
  { label: "Autoplay", value: "autoplay" },
  { label: "Loop", value: "loop" },
  { label: "Muted", value: "muted" },
  { label: "Poster", value: "poster" },
  { label: "Preload", value: "preload" },
  { label: "Srcset", value: "srcset" },
  { label: "Sizes", value: "sizes" },
  { label: "Cite", value: "cite" },
]
